import { useCallback, useReducer, useRef, useEffect, ReactNode } from 'react'
import { CatalogFilters, CatalogOrder, CatalogSubscriptionStatus } from '../../types'
import {
  CatalogFilterActionAddFilter,
  CatalogFilterActions,
  FILTERS_INITIAL_DATA,
  FiltersActionsContext,
  FiltersContext
} from './context'
import { reducer } from './reducer'

export function FiltersProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, FILTERS_INITIAL_DATA.state)

  const addFilterValue = useCallback(
    (payload: CatalogFilterActionAddFilter) => {
      dispatch({ type: CatalogFilterActions.addFiltersValue, payload })
    },
    [dispatch]
  )

  const setFilters = useCallback(
    (payload: Partial<CatalogFilters>) => {
      dispatch({ type: CatalogFilterActions.setFilters, payload })
    },
    [dispatch]
  )

  const setPage = useCallback(
    (payload: number) => {
      dispatch({ type: CatalogFilterActions.setPage, payload })
    },
    [dispatch]
  )

  const setOrderBy = useCallback(
    (payload: CatalogOrder) => {
      dispatch({ type: CatalogFilterActions.setOrderBy, payload })
    },
    [dispatch]
  )

  const setSubscribedBy = useCallback(
    (payload: CatalogSubscriptionStatus) => {
      dispatch({ type: CatalogFilterActions.setSubscribedBy, payload })
    },
    [dispatch]
  )

  const setFullTextSearch = useCallback(
    (payload: string) => {
      dispatch({ type: CatalogFilterActions.setFullTextSearch, payload })
    },
    [dispatch]
  )

  const setFiltersShown = useCallback(
    (payload: boolean) => {
      dispatch({ type: CatalogFilterActions.setFiltersShown, payload })
    },
    [dispatch]
  )

  const clearFilters = useCallback(() => {
    dispatch({ type: CatalogFilterActions.clearFilters })
  }, [dispatch])

  const clearAll = useCallback(() => {
    dispatch({ type: CatalogFilterActions.clearAll })
  }, [dispatch])

  const setClosedManually = useCallback(
    (payload: string) => {
      dispatch({ type: CatalogFilterActions.setClosedManually, payload })
    },
    [dispatch]
  )

  const clearCloseManually = useCallback(() => {
    dispatch({ type: CatalogFilterActions.clearCloseManually })
  }, [dispatch])

  useEffect(() => {
    if (state.page !== 1) {
      setPage(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.filters, state.subscribedBy, state.fullTextSearch, state.orderBy])

  const actions = useRef({
    setClosedManually,
    clearCloseManually,
    setFiltersShown,
    setPage,
    addFilterValue,
    setFilters,
    clearFilters,
    clearAll,
    setOrderBy,
    setFullTextSearch,
    setSubscribedBy
  })

  return (
    <FiltersActionsContext.Provider value={actions.current}>
      <FiltersContext.Provider value={state}>{children}</FiltersContext.Provider>
    </FiltersActionsContext.Provider>
  )
}
